import {
  interaction,
  isWeb,
  Stack,
  useMedia,
  XStack,
  XStackProps,
} from '@mythical/ui'
import { Avatar } from 'app/components/primitive/Avatar'
import { Button, ButtonLink } from 'app/components/primitive/Button'
import {
  IconBell,
  IconChevronLeft,
  IconDiscord,
  IconMenu,
} from 'app/components/primitive/Icon'
import useAnalytics from 'app/lib/analytics'
import { useStores } from 'app/store'
import { useEffect } from 'react'
import { useRouter } from 'solito/router'

import { MemberfulLink } from 'app/components/primitive/MemberfulLink'
import { SmartLink } from 'app/components/primitive/SmartLink'
import { useAuthSessionLogin } from 'app/hooks/use-auth-session-login'
import { useCurrentPath } from 'app/hooks/use-current-path'
import Search from '../Search'
import { Logo, LogoSmall } from './Logo'
import { useNavigationStore } from './NavigationStore'
import { NAV_HEIGHT, SIDEBAR_WIDTH } from './constants'
import { MobileNavigationMenu } from './MobileNavigationMenu'
import { useSafeArea } from 'app/provider/safe-area/use-safe-area'

interface NavigationProps {
  isLoggedIn?: boolean
  onLogin?: () => void
  user?: {
    profileImage?: string
  }
  canMemberfulLink?: boolean
}

const DesktopNavigation = ({
  isLoggedIn,
  user,
  canMemberfulLink = false,
  onLogin,
  ...props
}: NavigationProps & XStackProps) => {
  return (
    <XStack ai="center" {...props}>
      {isLoggedIn ? (
        <XStack ai="center" gap="$2.5">
          {isLoggedIn && !isWeb && (
            <ButtonLink
              href="/notifications"
              theme="secondary"
              circular
              aria-label="Notifications"
              size="$3.5"
              icon={<IconBell size={24} />}
            />
          )}
          <SmartLink
            Component={Button}
            href="https://mythicalsociety.memberful.com/account/discord/authorize"
            icon={<IconDiscord size={24} />}
            circular
            size="$3.5"
            theme="secondary"
          />
          <SmartLink
            Component={Avatar}
            href="/user/profile"
            bw={3}
            boc="$fgBrand"
            width={40}
            height={40}
            src={user?.profileImage}
            {...interaction.opacity}
          />
        </XStack>
      ) : (
        <XStack ai="center" gap="$3">
          <XStack gap="$2" ai="center">
            {canMemberfulLink && (
              <ButtonLink size="$3" br="$pill" href="/perks" uppercase>
                Sign up
              </ButtonLink>
            )}

            <Button
              theme="secondary"
              size="$3"
              br="$pill"
              onPress={onLogin}
              uppercase
            >
              Log in
            </Button>
          </XStack>
        </XStack>
      )}
    </XStack>
  )
}

interface MobileNavigationProps extends NavigationProps {}

const MobileNavigation = ({
  isLoggedIn,
  user,
  onLogin,
  canMemberfulLink = false,
  ...props
}: MobileNavigationProps & XStackProps) => {
  const toggleNav = useNavigationStore((state) => state.toggleNav)

  return (
    <XStack alignItems="center" gap="$3" {...props}>
      {!isLoggedIn && (
        <XStack alignItems="center" gap="$2.5">
          {canMemberfulLink && (
            <ButtonLink size="$3" br="$pill" href="/perks" uppercase>
              Sign up
            </ButtonLink>
          )}
          {onLogin && (
            <Button
              theme="secondary"
              size="$3"
              br="$pill"
              onPress={onLogin}
              uppercase
            >
              Log in
            </Button>
          )}
        </XStack>
      )}

      <XStack gap="$2">
        {isLoggedIn && (
          <MemberfulLink
            Component={Button}
            href="https://mythicalsociety.memberful.com/account/discord/authorize"
            theme="secondary"
            circular
            aria-label="Discord"
            size="$3.5"
            icon={<IconDiscord size={24} />}
            children={null}
          />
        )}
        {isLoggedIn && !isWeb && (
          <ButtonLink
            href="/notifications"
            theme="secondary"
            circular
            aria-label="Notifications"
            size="$3.5"
            icon={<IconBell size={24} />}
          />
        )}
        <Button
          theme="secondary"
          onPress={toggleNav}
          circular
          aria-label="Open sidebar"
          size="$3.5"
          icon={<IconMenu size={24} />}
        />
      </XStack>
    </XStack>
  )
}

interface BackButtonAndLogoProps {
  canGoBack: boolean
  canGoHome: boolean
}

const BackButtonAndLogo = ({
  canGoBack,
  canGoHome,
}: BackButtonAndLogoProps) => {
  const router = useRouter()
  return (
    <XStack
      animation="medium"
      ai="center"
      gap="$2.5"
      {...(isWeb && { $gtMd: { x: -38 } })}
      x={canGoBack ? 0 : -38}
    >
      <Button
        circular
        icon={<IconChevronLeft size={16} color="$white" />}
        aria-label="Go back"
        size="$2"
        theme="secondary"
        onPress={() => router.back()}
      />

      <SmartLink
        key="logo"
        Component={Logo}
        dsp="none"
        $gtSm={{ dsp: 'flex' }}
        {...(canGoHome && {
          href: isWeb ? '/' : '/homescreen',
          'aria-label': 'Back to homepage',
          ...interaction.opacity,
        })}
      />
      <SmartLink
        key="logo-small"
        Component={LogoSmall}
        $gtSm={{ dsp: 'none' }}
        {...(canGoHome && {
          href: isWeb ? '/' : '/homescreen',
          'aria-label': 'Back to homepage',
          ...interaction.opacity,
        })}
      />
    </XStack>
  )
}

export interface MainNavigationProps {
  showBackWeb?: boolean
  hideSearch?: boolean
  minimal?: boolean
  canGoHome?: boolean
}

const Navigation = ({
  showBackWeb,
  hideSearch,
  minimal = false,
  canGoHome = true,
}: MainNavigationProps) => {
  const { canGoBack: showBackNative, path } = useCurrentPath()

  const { userStore } = useStores()
  const analytics = useAnalytics()
  const router = useRouter()
  const insets = useSafeArea()

  const media = useMedia()
  const { startLogin } = useAuthSessionLogin()
  const isLoggedIn = !!userStore?.user?.id
  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  const userProps = {
    profileImage: userStore?.user?.profileImage,
  }

  const handleLogin = () => {
    if (isWeb) {
      const loginLink = userStore?.generateLoginUrl() || '/'
      router.push(loginLink)
    } else {
      startLogin()
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!userStore?.user?.id) {
      analytics.logUser(null)
    } else {
      analytics.logUser(userStore?.user?.id, userStore?.user?.tier || 'free')
    }
  }, [userStore?.user?.id])

  const canGoBack = showBackWeb || showBackNative
  const isSearchScreen = path === '/search'

  if (path === '/pre-home') return null

  return (
    <>
      {!isWeb && (
        <Stack pos="absolute" h={insets.top} zi="$5" w="100%" bg="$bgPrimary" />
      )}
      <XStack
        position={isWeb ? ('fixed' as 'absolute') : 'absolute'}
        fd="row"
        py="$3"
        top={insets.top}
        px="$3.5"
        height={NAV_HEIGHT}
        zIndex="$5"
        ai="center"
        bg="$bgPrimary"
        w="100%"
        jc="space-between"
        $gtMd={{ px: '$6' }}
        ov="hidden"
        gap="$3.5"
      >
        <XStack
          jc="flex-start"
          ov="hidden"
          gap="$2.5"
          minWidth={80}
          $gtMd={{ minWidth: SIDEBAR_WIDTH - 34 }}
        >
          <BackButtonAndLogo canGoBack={canGoBack} canGoHome={canGoHome} />
        </XStack>

        {!minimal && (
          <XStack
            fg={1}
            fs={1}
            ai="center"
            jc={hideSearch ? 'flex-end' : 'center'}
          >
            {!hideSearch && !media.md && (
              <XStack
                fg={1}
                fs={1}
                jc="center"
                dsp="none"
                $gtMd={{ dsp: 'flex', w: '100%', mx: 'auto', maw: 767 }}
              >
                <Search id="desktop-search" hideFilters={!isSearchScreen} />
              </XStack>
            )}

            <XStack pl="$3.5" jc="flex-end" ml="auto">
              <DesktopNavigation
                dsp="none"
                $gtMd={{ dsp: 'flex' }}
                isLoggedIn={isLoggedIn}
                onLogin={handleLogin}
                user={userProps}
                canMemberfulLink={canMemberfulLink}
              />

              <MobileNavigation
                isLoggedIn={isLoggedIn}
                user={userProps}
                onLogin={handleLogin}
                canMemberfulLink={canMemberfulLink}
                $gtMd={{ dsp: 'none' }}
              />
            </XStack>
          </XStack>
        )}
      </XStack>
      {isWeb && !minimal && <MobileNavigationMenu />}
    </>
  )
}

export default Navigation
